import React, { useState, useRef, useEffect, useCallback } from 'react';
import SunEditor from 'suneditor-react';
import { notifyError, notifySuccess } from '../../services/notify';
import { upload } from '../../services/utilities';
import { API_URI } from '../../services/constants';

// True when the page is served over HTTPS or on localhost — the only contexts
// where the browser allows SpeechRecognition.
const isSecureContext =
	window.isSecureContext ||
	window.location.protocol === 'https:' ||
	window.location.hostname === 'localhost' ||
	window.location.hostname === '127.0.0.1';

const nativeSpeechSupported = isSecureContext && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

/**
 * SunEditorWithSpeech
 *
 * Drop-in replacement for SunEditor that adds a "Voice Input" button.
 *
 * Two modes (auto-detected):
 *  • Secure context (HTTPS / localhost) → uses browser-native Web SpeechRecognition
 *    for real-time streaming transcription.
 *  • Insecure context (plain HTTP, private-network IP) → records audio via
 *    MediaRecorder and uploads it to the existing /transcription backend endpoint
 *    (AssemblyAI) that works over any HTTP connection.
 *
 * All standard SunEditor props are forwarded unchanged.
 */
const SunEditorWithSpeech = ({ onChange, setContents, ...rest }) => {
	// ── shared state ──────────────────────────────────────────────────────────
	const [isListening, setIsListening] = useState(false);
	const [interimText, setInterimText] = useState('');
	const [isTranscribing, setIsTranscribing] = useState(false);
	const currentContentRef = useRef(setContents || '');

	useEffect(() => {
		currentContentRef.current = setContents || '';
	}, [setContents]);

	const handleEditorChange = useCallback(
		content => {
			currentContentRef.current = String(content);
			onChange?.(String(content));
		},
		[onChange]
	);

	// ── Mode A: native SpeechRecognition (HTTPS / localhost) ──────────────────
	const recognitionRef = useRef(null);

	useEffect(() => {
		if (!nativeSpeechSupported) return;

		const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
		const recognition = new SpeechRecognition();
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = 'en-US';

		recognition.onresult = event => {
			let finalTranscript = '';
			let interim = '';
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const t = event.results[i][0].transcript;
				if (event.results[i].isFinal) {
					finalTranscript += t + ' ';
				} else {
					interim += t;
				}
			}
			setInterimText(interim);
			if (finalTranscript) {
				const newValue = (currentContentRef.current || '') + finalTranscript;
				currentContentRef.current = newValue;
				onChange?.(newValue);
			}
		};

		recognition.onerror = event => {
			setIsListening(false);
			setInterimText('');
			if (event.error === 'not-allowed') {
				notifyError('Microphone access denied. Please allow microphone access to use voice input.');
			} else if (event.error !== 'no-speech') {
				notifyError(`Voice input error: ${event.error}`);
			}
		};

		recognition.onend = () => {
			setIsListening(false);
			setInterimText('');
		};

		recognitionRef.current = recognition;
		return () => recognition.stop();
	}, []);

	useEffect(() => {
		return () => {
			if (recognitionRef.current && isListening) recognitionRef.current.stop();
		};
	}, [isListening]);

	const toggleNativeListening = useCallback(() => {
		if (!recognitionRef.current) {
			notifyError('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
			return;
		}
		if (isListening) {
			recognitionRef.current.stop();
			setIsListening(false);
			setInterimText('');
		} else {
			try {
				recognitionRef.current.start();
				setIsListening(true);
			} catch (err) {
				console.error('Failed to start speech recognition:', err);
				notifyError('Failed to start voice input. Please try again.');
			}
		}
	}, [isListening]);

	// ── Mode B: MediaRecorder → /transcription backend (plain HTTP) ───────────
	const mediaRecorderRef = useRef(null);
	const streamRef = useRef(null);
	const chunksRef = useRef([]);

	const stopStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach(t => t.stop());
			streamRef.current = null;
		}
	}, []);

	const startRecording = useCallback(async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;
			chunksRef.current = [];

			const recorder = new MediaRecorder(stream);
			recorder.ondataavailable = e => {
				if (e.data.size > 0) chunksRef.current.push(e.data);
			};
			recorder.onstop = async () => {
				stopStream();
				setIsListening(false);
				const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
				chunksRef.current = [];
				await sendToBackend(blob);
			};
			recorder.onerror = () => {
				stopStream();
				setIsListening(false);
				notifyError('Recording error. Please try again.');
			};

			mediaRecorderRef.current = recorder;
			recorder.start();
			setIsListening(true);
		} catch (err) {
			console.error('Microphone error:', err);
			if (err.name === 'NotAllowedError') {
				notifyError('Microphone access denied. Please allow microphone access to use voice input.');
			} else {
				notifyError('Could not access microphone. Please check your device settings.');
			}
		}
	}, [stopStream]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			mediaRecorderRef.current.stop();
		}
	}, []);

	const sendToBackend = useCallback(
		async blob => {
			try {
				setIsTranscribing(true);
				const formData = new FormData();
				formData.append('audio', blob, 'recording.webm');
				const result = await upload(`${API_URI}/transcription`, 'POST', formData);
				if (result?.text) {
					const newValue = (currentContentRef.current || '') + result.text + ' ';
					currentContentRef.current = newValue;
					onChange?.(newValue);
					notifySuccess('Audio transcribed successfully.');
				} else {
					notifyError('Transcription returned no text. Please try again.');
				}
			} catch (err) {
				console.error('Transcription error:', err);
				notifyError('Failed to transcribe audio. Please try again.');
			} finally {
				setIsTranscribing(false);
			}
		},
		[onChange]
	);

	// Clean up recorder on unmount
	useEffect(() => {
		return () => {
			if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
				mediaRecorderRef.current.stop();
			}
			stopStream();
		};
	}, [stopStream]);

	const toggleRecording = useCallback(() => {
		if (isListening) {
			stopRecording();
		} else {
			startRecording();
		}
	}, [isListening, startRecording, stopRecording]);

	// ── unified button handler ────────────────────────────────────────────────
	const handleVoiceToggle = nativeSpeechSupported ? toggleNativeListening : toggleRecording;
	const buttonBusy = isListening || isTranscribing;

	// ── render ────────────────────────────────────────────────────────────────
	return (
		<div>
			{/* Voice input button */}
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
				<button
					type="button"
					onClick={handleVoiceToggle}
					disabled={isTranscribing}
					className={`btn btn-sm ${buttonBusy ? 'btn-danger' : 'btn-outline-secondary'}`}
					style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
					title={isListening ? 'Stop recording' : 'Start voice input'}
				>
					{isTranscribing ? (
						<>
							<span
								style={{
									width: '10px',
									height: '10px',
									border: '2px solid #fff',
									borderTop: '2px solid transparent',
									borderRadius: '50%',
									animation: 'sews-spin 0.7s linear infinite',
									display: 'inline-block',
								}}
							/>
							Transcribing…
						</>
					) : (
						<>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
								<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
								<line x1="12" y1="19" x2="12" y2="23" />
								<line x1="8" y1="23" x2="16" y2="23" />
							</svg>
							{isListening ? (
								<>
									<span
										style={{
											width: '8px',
											height: '8px',
											background: '#fff',
											borderRadius: '50%',
											animation: 'sews-pulse 1.5s infinite',
										}}
									/>
									{nativeSpeechSupported ? 'Recording…' : 'Recording… (click to stop)'}
								</>
							) : (
								'Voice Input'
							)}
						</>
					)}
				</button>
			</div>

			{/* Editor */}
			<SunEditor {...rest} setContents={setContents} onChange={handleEditorChange} />

			{/* Listening indicator (native mode only — shows interim text) */}
			{isListening && nativeSpeechSupported && (
				<div
					style={{
						marginTop: '8px',
						padding: '10px 14px',
						background: '#fff3cd',
						border: '1px solid #ffc107',
						borderRadius: '4px',
						fontSize: '12px',
						color: '#856404',
						display: 'flex',
						alignItems: 'center',
						gap: '10px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
						{[0, 0.2, 0.4].map((delay, idx) => (
							<span
								key={idx}
								style={{
									width: '6px',
									height: '6px',
									background: '#dc3545',
									borderRadius: '50%',
									animation: `sews-pulse 1s infinite ${delay}s`,
								}}
							/>
						))}
					</div>
					<div style={{ flex: 1 }}>
						<strong>Listening…</strong> Speak clearly. Text will be appended to your content.
						{interimText && (
							<span style={{ display: 'block', marginTop: '4px', fontStyle: 'italic', color: '#6c757d' }}>"{interimText}"</span>
						)}
					</div>
					<button
						type="button"
						onClick={toggleNativeListening}
						className="btn btn-danger btn-sm"
						style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
					>
						Stop
					</button>
				</div>
			)}

			{/* Recording indicator (backend mode) */}
			{isListening && !nativeSpeechSupported && (
				<div
					style={{
						marginTop: '8px',
						padding: '10px 14px',
						background: '#f8d7da',
						border: '1px solid #f5c6cb',
						borderRadius: '4px',
						fontSize: '12px',
						color: '#721c24',
						display: 'flex',
						alignItems: 'center',
						gap: '10px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
						{[0, 0.2, 0.4].map((delay, idx) => (
							<span
								key={idx}
								style={{
									width: '6px',
									height: '6px',
									background: '#dc3545',
									borderRadius: '50%',
									animation: `sews-pulse 1s infinite ${delay}s`,
								}}
							/>
						))}
					</div>
					<div style={{ flex: 1 }}>
						<strong>Recording…</strong> Click "Stop &amp; Transcribe" when done. Audio will be sent to the server for transcription.
					</div>
					<button
						type="button"
						onClick={stopRecording}
						className="btn btn-danger btn-sm"
						style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
					>
						Stop &amp; Transcribe
					</button>
				</div>
			)}

			<style>{`
				@keyframes sews-pulse {
					0%, 100% { opacity: 1; transform: scale(1); }
					50% { opacity: 0.4; transform: scale(0.75); }
				}
				@keyframes sews-spin {
					from { transform: rotate(0deg); }
					to   { transform: rotate(360deg); }
				}
			`}</style>
		</div>
	);
};

export default SunEditorWithSpeech;
